"use client";

import { c as _c } from "react/compiler-runtime";
import { evmChains } from "@b3/chain-registry";
import { useQueryClient } from "@tanstack/react-query";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { subscriptionKeys } from "@/hooks/useSubscription";
import { verifyIncentiveTask } from "@/lib/api/incentive";
import { cn } from "@/lib/utils";
import { IncentiveHowItWorks } from "./IncentiveHowItWorks";
import { IncentiveProgress } from "./IncentiveProgress";
import { IncentiveTaskCard } from "./IncentiveTaskCard";
import { TASKS_BY_TYPE } from "./tasks";
import { TreasuryModal } from "./TreasuryModal";
import { useIncentiveState } from "./useIncentiveState";
const TREASURY_SUPPORTED_CHAINS: {
  chainId: number;
  name: string;
}[] = evmChains.filter(c => !c.isTestnet).map(c => ({
  chainId: c.chainId,
  name: c.name
}));

// Tracks "already auto-verified this (user, org) pair this page session".
// Backend claims are idempotent; this only prevents StrictMode/re-render fan-out.
const autoVerifiedSession = new Set<string>();
export type IncentivePanelVariant = "page" | "billing" | "onboarding";
interface IncentivePanelProps {
  variant?: IncentivePanelVariant;
  className?: string;
  showHeader?: boolean;
  showBackLink?: boolean;
  showHowItWorks?: boolean;
}
function SkeletonCards(t0) {
  const $ = _c(7);
  const {
    compact
  } = t0;
  const t1 = compact ? "grid-cols-[repeat(auto-fit,minmax(min(100%,22rem),1fr))]" : "auto-rows-fr md:grid-cols-2 md:gap-4";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("grid grid-cols-1 items-stretch gap-3", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== compact) {
    t3 = Array.from({
      length: compact ? 4 : 6
    }).map(_temp);
    $[2] = compact;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function _temp(_, i) {
  return <div key={i} className="border-ds-border-default bg-ds-surface-card shadow-ds-soft rounded-xl border p-4"><div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 h-5 w-32 animate-pulse rounded" /><div className="bg-ds-neutral-200/80 dark:bg-ds-neutral-700/80 mt-2 h-4 w-48 animate-pulse rounded" /><div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 mt-4 h-9 w-full animate-pulse rounded" /></div>;
}
function ErrorState(t0) {
  const $ = _c(9);
  const {
    onRetry,
    compact
  } = t0;
  const t1 = compact ? "px-4 py-8" : "px-6 py-12";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("border-ds-border-default bg-ds-surface-card shadow-ds-soft rounded-xl border text-center", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="text-ds-text-primary text-sm font-semibold">Failed to load rewards.</p>;
    t4 = <p className="text-ds-text-muted mt-1 text-sm">Refresh the task list and try again.</p>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== onRetry) {
    t5 = <Button size="sm" variant="white" onClick={onRetry} className="mt-4">Retry</Button>;
    $[4] = onRetry;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t2 || $[7] !== t5) {
    t6 = <div className={t2}>{t3}{t4}{t5}</div>;
    $[6] = t2;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
function IncentiveHeader(t0) {
  const $ = _c(22);
  const {
    variant,
    totalCu,
    totalAi,
    showBackLink
  } = t0;
  const isBilling = variant === "billing";
  const t1 = isBilling && "border-ds-border-default bg-ds-surface-card shadow-ds-soft rounded-xl border p-5 sm:p-6";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex flex-col gap-3", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== showBackLink) {
    t3 = showBackLink ? <Link href="/dashboard" className="text-ds-text-muted hover:text-ds-text-secondary text-sm transition-colors">← Back to dashboard</Link> : null;
    $[2] = showBackLink;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="text-ds-text-muted font-mono text-xs font-medium uppercase">Launch rewards</p>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = isBilling ? "text-2xl" : "text-3xl";
  let t6;
  if ($[5] !== t5) {
    t6 = cn("text-ds-text-primary font-semibold", t5);
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t6) {
    t7 = <h1 className={t6}>Earn extra credits</h1>;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="text-ds-text-muted mt-1 max-w-2xl text-sm">Complete optional setup tasks to add compute units and AI credits to this organization.</p>;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== t7) {
    t9 = <div>{t4}{t7}{t8}</div>;
    $[10] = t7;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== totalAi || $[13] !== totalCu) {
    t10 = totalCu != null && totalAi != null ? <div className="border-ds-border-default bg-ds-surface-muted rounded-lg border px-3 py-2 text-sm"><span className="text-ds-text-muted">Available:</span>{" "}<span className="text-ds-text-primary font-semibold tabular-nums">{totalCu.toLocaleString()} CU</span><span className="text-ds-text-disabled"> + </span><span className="text-ds-text-primary font-semibold tabular-nums">{totalAi.toLocaleString()} AI</span></div> : null;
    $[12] = totalAi;
    $[13] = totalCu;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t10 || $[16] !== t9) {
    t11 = <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">{t9}{t10}</div>;
    $[15] = t10;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t2 || $[20] !== t3) {
    t12 = <header className={t2}>{t3}{t11}</header>;
    $[18] = t11;
    $[19] = t2;
    $[20] = t3;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  return t12;
}
export function IncentivePanel({
  variant = "page",
  className,
  showHeader = variant !== "onboarding",
  showBackLink = variant === "page",
  showHowItWorks = variant !== "onboarding"
}: IncentivePanelProps) {
  const {
    state,
    loading,
    error,
    refetch
  } = useIncentiveState();
  const [treasuryOpen, setTreasuryOpen] = useState(false);
  const qc = useQueryClient();
  const compact = variant === "onboarding";
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const taskType = params.get("oauth_redirect_for") ?? params.get("yt_redirect_for");
    if (!taskType) return;
    const meta = TASKS_BY_TYPE[taskType];
    if (!meta?.externalURL) return;
    window.location.replace(meta.externalURL);
  }, []);
  const handleClaim = () => {
    refetch();
    void qc.invalidateQueries({
      queryKey: subscriptionKeys.all
    });
  };
  useEffect(() => {
    if (!state) return;
    const internalPending = state.tasks.filter(t => t.status === "pending" && TASKS_BY_TYPE[t.type]?.isInternal === true);
    if (internalPending.length === 0) return;
    const gateKey = `${state.user_id}:${state.org_id}`;
    if (autoVerifiedSession.has(gateKey)) return;
    autoVerifiedSession.add(gateKey);
    let cancelled = false;
    Promise.all(internalPending.map(t => verifyIncentiveTask(t.type).catch(() => null))).then(() => {
      if (!cancelled) refetch();
    });
    return () => {
      cancelled = true;
    };
    // Intentional: only re-run when org changes — not on every state.tasks change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.org_id]);
  const claimedCount = useMemo(() => state?.tasks.filter(t => t.status === "claimed").length ?? 0, [state]);
  const raw = state?.tasks.find(t => t.type === "fund_treasury")?.metadata?.wallet_address;
  const treasuryAddress = typeof raw === "string" ? raw : "";
  return <div className={cn(variant === "page" && "container mx-auto max-w-4xl py-8", variant === "billing" && "space-y-5", variant === "onboarding" && "space-y-4", className)}>
      {showHeader ? <IncentiveHeader variant={variant} totalCu={state?.totals.cu_max} totalAi={state?.totals.ai_max} showBackLink={showBackLink} /> : null}

      {loading ? <SkeletonCards compact={compact} /> : error || !state ? <ErrorState onRetry={refetch} compact={compact} /> : <>
          <IncentiveProgress totals={state.totals} totalTasks={state.tasks.length} claimedTasks={claimedCount} compact={compact} />

          <div className={cn("grid grid-cols-1 items-stretch gap-3", compact ? "grid-cols-[repeat(auto-fit,minmax(min(100%,22rem),1fr))]" : "auto-rows-fr md:grid-cols-2 md:gap-4")}>
            {state.tasks.map(task => {
          const meta = TASKS_BY_TYPE[task.type];
          if (!meta) return null;
          return <IncentiveTaskCard key={task.type} task={task} meta={meta} onClaim={handleClaim} onOpenTreasury={meta.type === "fund_treasury" ? () => setTreasuryOpen(true) : undefined} compact={compact} />;
        })}
          </div>

          {showHowItWorks ? <IncentiveHowItWorks /> : null}

          {treasuryAddress && <TreasuryModal open={treasuryOpen} onClose={() => setTreasuryOpen(false)} treasuryAddress={treasuryAddress} supportedChains={TREASURY_SUPPORTED_CHAINS} onClaimed={handleClaim} />}
        </>}
    </div>;
}